import { useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './cx'
import { OverlayPortalContext, useOverlayEscapeLayer, type OverlayPortalBoundary } from './OverlayPortal'
import s from './AnchoredSurface.module.css'

export interface AnchoredSurfaceProps {
  /** The element the surface hangs from. The host owns the trigger. */
  anchor: HTMLElement
  onClose: () => void
  children: ReactNode
  /** A fixed width in px, where a host needs the surface wider than its content. */
  width?: number
  className?: string
  testId?: string
}

/**
 * AN ANCHORED, NON-MODAL SURFACE (#2406 §3).
 *
 * Lifted verbatim from `apps/console/src/crm/bits.tsx`'s `Popover`, which
 * three console files rendered 60 times against a private stylesheet.
 *
 * ── why this exists BESIDE `Menu`, which is the obvious objection ──────────
 * `Menu` is Radix `DropdownMenu` and should stay the default: roving focus,
 * type-ahead, focus return and truthful `aria-expanded` are the parts of a menu
 * that are genuinely hard, and hand-rolling them is how a control ships looking
 * right and unusable without a mouse.
 *
 * But roughly HALF of what the console puts in these surfaces is not a menu.
 * The column-filter editor holds inputs and a select; the sort builder holds
 * nested buttons; the view switcher's rows each carry a second, independent pin
 * control. Radix would claim arrow keys and printable characters for
 * navigation, so typing into the filter's text field would move the highlight
 * instead of filling the field. A control that is not a menu must not claim
 * menu semantics — that is the rule, and this is the surface for the other
 * half.
 *
 * ── what it deliberately is not ────────────────────────────────────────────
 * Not `role="dialog"`: demoted at #387/UX-02 for the same reason `Lookup`'s
 * was. Nothing is trapped, the page behind stays live, and it carries no
 * accessible name — a screen reader announced the bare word "dialog" and then
 * promised modal semantics that do not exist. The CHILDREN carry the real
 * semantics, which is why `Item` below is a real `<button>`.
 */
export function AnchoredSurface({ anchor, onClose, children, width, className, testId }: AnchoredSurfaceProps) {
  const ref = useRef<HTMLDivElement>(null)
  /* THE SURFACE IS AN ESCAPE BOUNDARY (#2375 repair). A nested layer inside it
     — the column filter's open MultiSelect list, a DatePicker calendar —
     registers through `useOverlayEscapeLayer`, and while any is mounted the
     surface does NOT treat Escape as its own: the layer closes itself and the
     surface stays. The next Escape, with no layer left, closes the surface.
     Without this the capture-phase listener below always won, so Escape in an
     open list closed the whole editor — the list's own handler never ran. */
  const outer = useContext(OverlayPortalContext)
  /* …and it is itself a layer of whatever boundary holds IT (a Modal), so an
     Escape meant for the surface does not also close the dialog behind it. */
  useOverlayEscapeLayer()
  const escapeLayers = useRef(0)
  /* A nested custom overlay also belongs to THIS surface. Keeping its portal
     under this node makes an option click an inside click; inheriting the
     outer target made the child a sibling and the surface dismissed itself.
     The surface itself still mounts in the nearest outer boundary (a Modal). */
  const boundary = useMemo<OverlayPortalBoundary>(() => ({ target: ref, escapeLayers }), [])
  useLayoutEffect(() => {
    const el = ref.current!
    const r = anchor.getBoundingClientRect()
    el.style.left = Math.min(Math.max(8, r.left), innerWidth - el.offsetWidth - 8) + 'px'
    const top = r.bottom + 5 + el.offsetHeight > innerHeight - 8
      ? Math.max(8, innerHeight - el.offsetHeight - 8) : r.bottom + 5
    el.style.top = top + 'px'
  })
  useEffect(() => {
    const down = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node) && !anchor.contains(e.target as Node)) onClose()
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // A nested layer owns this Escape; it closes itself (see above).
      if (escapeLayers.current > 0) return
      e.stopPropagation()
      onClose()
      /* FOCUS RETURN, which the console copy did not do. Escape dismissed the
         surface and left focus on `<body>`, so the next Tab restarted at the
         top of the page — a keyboard reader lost their place every time they
         closed one of these. Radix does this for `Menu`; here it is explicit. */
      anchor.focus()
    }
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key, true)
    return () => {
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('keydown', key, true)
    }
  }, [anchor, onClose])
  return createPortal(
    <div ref={ref} className={cx(s.surface, className)} style={{ width }}
      data-anchored-surface="" data-testid={testId ?? 'crm-popover'}>
      <OverlayPortalContext.Provider value={boundary}>{children}</OverlayPortalContext.Provider>
    </div>,
    outer?.target.current ?? document.body)
}

/** A command inside the surface. A real `<button>`, so it is in the tab order
 *  and answers Enter and Space without this file implementing either. */
AnchoredSurface.Item = function SurfaceItem({ children, onClick, active, disabled, testId, title, trailing }: {
  children: ReactNode
  onClick?: () => void
  /** Offered but not available — the last visible column cannot be hidden. A
   *  disabled control is still ANNOUNCED, which is why it is not simply absent:
   *  the reader needs to know the option exists and why it is out of reach. */
  disabled?: boolean
  /** The pointer/keyboard highlight a host drives itself (a type-ahead list). */
  active?: boolean
  testId?: string
  title?: string
  /**
   * A SECOND, independent control on the row — a pin, a remove, an open-in-new.
   *
   * It exists because the console had one and put it INSIDE the row's button:
   * a `<button>` nested in a `<button>`, which is invalid HTML, which is why it
   * needed `stopPropagation` to work at all, and which a screen reader resolves
   * by flattening or by dropping one of them. Rendered as a SIBLING here, so
   * both are real controls and neither has to fight the other's click.
   */
  trailing?: ReactNode
}) {
  const item = (
    <button type="button" className={s.item} data-active={active || undefined}
      disabled={disabled} data-testid={testId} title={title} onClick={onClick}>{children}</button>
  )
  if (trailing == null) return item
  return <div className={s.row}>{item}{trailing}</div>
}

/** A heading over a run of items — what the group below is. */
AnchoredSurface.Section = function SurfaceSection({ children, testId }: {
  children: ReactNode
  testId?: string
}) {
  return <div className={s.section} data-testid={testId}>{children}</div>
}

AnchoredSurface.Divider = function SurfaceDivider() {
  return <div className={s.divider} />
}

/**
 * The tick column. It renders its box whether or not it is checked, which is
 * the whole reason it is a component: a list whose labels jump 14px sideways
 * when a selection lands is a list that is hard to read down.
 */
AnchoredSurface.Check = function SurfaceCheck({ checked }: { checked: boolean }) {
  return <span className={s.check} aria-hidden="true">{checked ? '✓' : ''}</span>
}
